import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { Observable, of, Subject } from "rxjs";
import { finalize, map, startWith, takeUntil } from "rxjs/operators";
import { ErrorType } from "src/app/enums/error-type.enum";
import endpoints from "src/app/helpers/endpoints";
import { ApiService } from "src/app/services/api.service";
import * as moment from "moment";

@Component({
  selector: "fury-create-purchase-item",
  templateUrl: "./create-purchase-item.component.html",
  styleUrls: ["./create-purchase-item.component.scss"],
})
export class CreatePurchaseItemComponent implements OnInit, OnDestroy {
  ngUnsubscribe = new Subject();
  purchaseItem = new FormGroup({
    itemId: new FormControl("", [Validators.required]),
    itemName: new FormControl("", [Validators.required]),
    itemParts: new FormControl(""),
    color: new FormControl(""),
    orderDate: new FormControl("", [Validators.required]),
    supplier: new FormControl(""),
    orderNumber: new FormControl(""),
    tracking: new FormControl(""),
    deliveryDate: new FormControl(""),
    totalAmount: new FormControl("", [Validators.required]),
    specialNotes: new FormControl("", [Validators.required]),
    status: new FormControl("", [Validators.required]),
  });
  coverItems = [];
  searchCoverItemList: Observable<any[]> = of([]);
  statusList = ["Ordered", "In Transit", "Received"];
  minDate = new Date().toISOString().split("T")[0];

  constructor(
    public dialogRef: MatDialogRef<CreatePurchaseItemComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { claimId: any; purchaseItem: any },
    private apiService: ApiService
  ) {}

  ngOnInit() {
    this.getCoverItems();
    this.searchCoverItems();
  }

  onNoClick() {
    this.dialogRef.close();
  }
  getCoverItems() {
    this.apiService
      .get(`${endpoints.settings.service.getCoverItems}?page=1&size=1000`)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.coverItems = res.body.planCoverItems?.data;
            this.purchaseItem.get("itemName").setValue("");
            if(this.data.purchaseItem) this.setPurchaseItemData();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.apiService.alert(error?.message, ErrorType.Error);
        },
      });
  }
  searchCoverItems() {
    setTimeout(() => {
      this.searchCoverItemList = this.purchaseItem
        .get("itemName")!
        .valueChanges.pipe(
          startWith(""),
          map((value) =>
            value ? this.filterCoverItems(value) : this.coverItems.slice()
          )
        );
    }, 100);
  }
  filterCoverItems(name: string) {
    let arr = this.coverItems.filter(
      (item: any) => item?.title.toLowerCase().indexOf(name.toLowerCase()) === 0
    );
    return arr?.length ? arr : [{ title: "No item found", id: 0 }];
  }
  onchangeCoverItem(event: string) {
    let selectedItem = this.coverItems.find((item) => item?.title === event);
    this.purchaseItem.patchValue({
      itemId: selectedItem ? selectedItem?.planCoverItemId : "",
    });
  }
  addEditPurchaseItem() {
    if (!this.purchaseItem.value.itemId && this.purchaseItem.value.itemName) {
      this.apiService.alert(
        "Please select a valid covered item",
        ErrorType.Error
      );
      return;
    }
    if (this.purchaseItem.valid) {
      const endpoint = this.data.purchaseItem
        ? endpoints.fileAClaim.updatePurchase(
            this.data.purchaseItem?.claimPurchaseItemId
          )
        : endpoints.fileAClaim.createPurchase;
      const payload = this.createPayload();
      this.apiService
        .post(endpoint, payload)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (res: HttpResponse<any>) => {
            this.apiService.alert(res?.body?.message, ErrorType.Success);
            this.dialogRef.close(res);
          },
          error: (error: HttpErrorResponse) => {
            this.apiService.alert(error?.message, ErrorType.Error);
          },
        });
    } else {
      this.purchaseItem.markAllAsTouched();
    }
  }
  createPayload() {
    const payload: any = {
      itemId: this.purchaseItem.value.itemId,
      itemName: this.purchaseItem.value.itemName,
      itemParts: this.purchaseItem.value.itemParts,
      color: this.purchaseItem.value.color,
      orderDate: this.purchaseItem.value.orderDate
        ? moment(this.purchaseItem.value.orderDate).format("YYYY-MM-DD")
        : "",
      supplier: this.purchaseItem.value.supplier,
      orderNumber: this.purchaseItem.value.orderNumber,
      tracking: this.purchaseItem.value.tracking,
      deliveryDate: this.purchaseItem.value.deliveryDate
        ? moment(this.purchaseItem.value.deliveryDate).format("YYYY-MM-DD")
        : "",
      totalAmount: this.purchaseItem.value.totalAmount.toString(),
      specialNotes: this.purchaseItem.value.specialNotes,
      status: this.purchaseItem.value.status,
    };
    if (this.data.purchaseItem) {
      payload.claimId = this.data.claimId;
    } else {
      payload.claimIds = this.data.claimId;
    }
    return payload;
  }
  setPurchaseItemData() {
    this.purchaseItem.patchValue({
      itemName: this.data.purchaseItem?.itemName,
      itemParts: this.data.purchaseItem?.itemParts,
      color: this.data.purchaseItem?.color,
      orderDate: this.data.purchaseItem?.orderDate ? new Date(this.data.purchaseItem?.orderDate) as any : "",
      supplier: this.data.purchaseItem?.supplier,
      orderNumber: this.data.purchaseItem?.orderNumber,
      tracking: this.data.purchaseItem?.tracking,
      deliveryDate: this.data.purchaseItem?.deliveryDate ? new Date(this.data.purchaseItem?.deliveryDate) as any : "",
      totalAmount: this.data.purchaseItem?.totalAmount,
      specialNotes: this.data.purchaseItem?.specialNotes,
      status: this.data.purchaseItem?.status,
    });
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
